import { defineStore } from 'pinia';
import { decodeJwt } from 'oidc-spa/tools/decodeJwt';
import { type Oidc } from 'oidc-spa';
import { useSessionContextStore } from './context';
import { delayMs } from '@/utils/time';
import { useApolloClient } from '@vue/apollo-composable';
import customers from '@/constants/customers';
import { LoadedFederalStateList, userToContextType, UserType } from '@/types';
import { CustomerId, InstitutionId, OrganizationId } from '@qmrs/entityid';
import gql from 'graphql-tag';

import { usePrivacyStore } from '@/stores/privacyStore';
import {
    FederalStateList,
    FederalStateListInput,
    PublicEntityItem,
    PublicEntityType
} from '@/graphql/types-service';
import { ContextType } from '@/graphql/types-service';

const url = 'https://id.shapth.homenet';
const realm = 'shapth';
const clientId = 'spa';
let oidc: Oidc | undefined;

export const useSessionStore = defineStore('session', () => {
    const actor = ref<PublicEntityItem | null>(null);
    const isLoading = ref(true);
    const isInitOnce = ref(false);
    const isChecking = ref(true);
    const isHealthy = ref(true);
    const sessionContext = useSessionContextStore();
    const selectedContext = useSelectedContextStore();
    const highlighter = useHighlighterStore();

    const federalStateList = ref<LoadedFederalStateList>(
        customers.map((c) => {
            return {
                key: c.key,
                id: new CustomerId(BigInt(parseInt(c.destatis, 10))),
                name: c.name
            };
        })
    );

    const menuStore = useMenuStore();
    const { client: serviceClient } = useApolloClient('service');

    function logout() {
        if (oidc?.isUserLoggedIn) {
            oidc.logout({ redirectTo: 'specific url', url: '/' });
        }
    }

    function getToken(): string | undefined {
        if (oidc?.isUserLoggedIn) {
            return oidc.getTokens().accessToken;
        }
        return undefined;
    }

    async function fetchActor(): Promise<void> {
        const result = await serviceClient.query<{ actor: PublicEntityItem }>({
            query: gql`
                query PublicEntityActor {
                    actor: publicEntityActor {
                        r
                        id
                        name
                        parent {
                            r
                            id
                            name
                            ty
                            kind
                        }
                        ty
                        kind
                        active
                    }
                }
            `,
            fetchPolicy: 'network-only'
        });
        actor.value = result.data?.actor ?? null;
    }

    async function reloadActor(): Promise<void> {
        if (isInitOnce.value === true) {
            try {
                isLoading.value = true;
                await fetchActor();
            } catch (error) {
                console.error(error);
                isHealthy.value = false;
            } finally {
                isLoading.value = false;
            }
        }
    }

    function updateMenu() {
        const isOwner = has('owner');
        switch (actor.value?.ty) {
            case PublicEntityType.Olb:
            case PublicEntityType.OlbB:
                menuStore.setOlbMenu();
                break;
            case PublicEntityType.Hdp:
                menuStore.setHdpMenu(
                    isOwner || sessionContext.isAdminOrSupport
                );
                break;
            case PublicEntityType.Lab:
                menuStore.setLabMenu(
                    isOwner || sessionContext.isAdminOrSupport
                );
                break;
            case PublicEntityType.LabB:
                menuStore.setLabBranchMenu(
                    isOwner || sessionContext.isAdminOrSupport
                );
                break;
            case PublicEntityType.Wsp:
                menuStore.setWspMenu(
                    isOwner || sessionContext.isAdminOrSupport
                );
                break;
            case PublicEntityType.WspB:
                menuStore.setWspBranchMenu(
                    isOwner || sessionContext.isAdminOrSupport
                );
                break;
            default:
                if (sessionContext.isAdminOrSupport) {
                    menuStore.setAdminMenu();
                }
                break;
        }
    }
    watch(actor, () => {
        updateMenu();
    });
    let initialize: null | Promise<void> = null;
    async function init() {
        if (typeof window === 'undefined') {
            return;
        }
        if (initialize !== null) {
            return initialize;
        }
        const extraQueryParams = {
            dark: isDark.value ? 'true' : 'false',
            ui_locales: 'de'
        };
        initialize = (async () => {
            isLoading.value = true;
            await highlighter.init();
            const { createOidc } = await import('oidc-spa');
            oidc = await createOidc({
                issuerUri: `${url}/realms/${realm}`,
                clientId,
                homeUrl: import.meta.env.BASE_URL
                // noIframe: false,
            });
            if (!oidc.isUserLoggedIn) {
                oidc.login({
                    doesCurrentHrefRequiresAuth: false,
                    extraQueryParams
                });
                return;
            }
            const tokens = oidc.getTokens();
            await sessionContext.init(decodeJwt(tokens.accessToken));
            await nextTick();
            if (sessionContext.isAdminOrSupport) {
                selectedContext.init(reloadActor);
                menuStore.setAdminMenu();
                menuStore.enable();
                try {
                    const result = await serviceClient.query<
                        {
                            list: FederalStateList;
                        },
                        { input: FederalStateListInput }
                    >({
                        query: gql`
                            query FederalStateList(
                                $input: FederalStateListInput
                            ) {
                                list: federalStateList(input: $input) {
                                    items {
                                        r
                                        id
                                        active
                                        modification {
                                            created {
                                                at
                                            }
                                            updated {
                                                at
                                            }
                                        }
                                    }
                                }
                            }
                        `,
                        variables: {
                            input: {
                                pagination: {
                                    page: 0,
                                    limit: 16
                                }
                            }
                        }
                    });
                    const list = result.data?.list?.items ?? [];
                    for (const el of list) {
                        const idx = federalStateList.value.findIndex(
                            (it) => it.id.toString() === el.r
                        );
                        if (idx !== -1) {
                            federalStateList.value[idx].exists =
                                typeof el.active === 'boolean';
                            federalStateList.value[idx].createdAt =
                                el.modification.created.at;
                            federalStateList.value[idx].updatedAt =
                                el.modification.updated.at;
                        }
                    }
                    isHealthy.value = federalStateList.value.every(
                        (it) => it.exists
                    );
                } catch (error) {
                    console.error(error);
                    isHealthy.value = false;
                    return;
                }
            }
            const privacy = usePrivacyStore();
            await privacy.init();
            isChecking.value = false;
            try {
                if (
                    sessionContext.type === UserType.Applicant ||
                    (sessionContext.type === UserType.Admin &&
                        selectedContext.type === UserType.None)
                ) {
                    await delayMs(700);
                } else {
                    await Promise.all([delayMs(700), fetchActor()]);
                }
                if (!sessionContext.isAdminOrSupport) {
                    updateMenu();
                }
            } catch (error) {
                console.error(error);
                isHealthy.value = false;
            }
            isInitOnce.value = true;
            isLoading.value = false;
        })();
        await initialize;
    }

    const context = computed(() => {
        if (sessionContext.isAdminOrSupport) {
            return selectedContext.contextId?.toString() ?? '';
        }
        return sessionContext.contextId?.toString() ?? '';
    });
    const contextId = computed(() => {
        if (sessionContext.isAdminOrSupport) {
            return selectedContext.contextId;
        }
        return sessionContext.contextId;
    });
    const federalState = computed(() => {
        if (sessionContext.isAdminOrSupport) {
            return federalStateList.value?.find(
                (it) => it.id?._id === selectedContext.rootId?._id
            );
        }
        if (sessionContext.contextId instanceof CustomerId) {
            return federalStateList.value?.find(
                (it) => it.id?._id === sessionContext.contextId!._id
            );
        }
        if (sessionContext.contextId instanceof OrganizationId) {
            return federalStateList.value?.find(
                (it) =>
                    it.id?._id ===
                    (sessionContext.contextId as OrganizationId)!._cid
            );
        }
        if (sessionContext.contextId instanceof InstitutionId) {
            return federalStateList.value?.find(
                (it) =>
                    it.id?._id ===
                    (sessionContext.contextId as OrganizationId)!._cid
            );
        }
    });
    const federalStateKey = computed(() => {
        if (federalState.value) {
            return federalState.value.key;
        }
        return 'DE';
    });
    const userType = computed(() => sessionContext.type);
    const contextType = computed(() => {
        if (sessionContext.isAdminOrSupport && selectedContext.sub?.ty) {
            return selectedContext.sub.ty;
        }
        return userToContextType(sessionContext.type);
    });
    const contextKind = computed(() => {
        if (sessionContext.isAdminOrSupport && selectedContext.sub?.ty) {
            return selectedContext.sub.kind;
        }
    });

    const activeContextType = computed(() => {
        if (sessionContext.isAdminOrSupport) {
            return selectedContext.contextType;
        }
        return userToContextType(sessionContext.type);
    });

    const isActiveAsAdmin = computed(() => {
        return (
            sessionContext.isAdminOrSupport &&
            activeContextType.value === ContextType.Sys
        );
    });

    const isApplicant = computed(
        () => sessionContext.type === UserType.Applicant
    );

    function canList(resource: string): boolean {
        return (
            isActiveAsAdmin.value ||
            sessionContext.roles.hasRealmRole(`${resource}:list`)
        );
    }

    function canCreate(resource: string): boolean {
        return (
            isActiveAsAdmin.value ||
            sessionContext.roles.hasRealmRole(`${resource}:create`)
        );
    }

    function canView(resource: string): boolean {
        return (
            isActiveAsAdmin.value ||
            sessionContext.roles.hasRealmRole(`${resource}:view`)
        );
    }

    function canUpdate(resource: string): boolean {
        return (
            isActiveAsAdmin.value ||
            sessionContext.roles.hasRealmRole(`${resource}:update`)
        );
    }

    function canDelete(resource: string): boolean {
        return (
            isActiveAsAdmin.value ||
            sessionContext.roles.hasRealmRole(`${resource}:delete`)
        );
    }

    function canManage(resource: string): boolean {
        return (
            isActiveAsAdmin.value ||
            sessionContext.roles.hasRealmRole(`${resource}:manage`)
        );
    }

    function has(ty: string): boolean {
        return sessionContext.roles.hasRealmRole(ty);
    }

    const canSeeModifications = computed(() => {
        return (
            sessionContext.isAdminOrSupport ||
            (has('olb') && has('owner') && !has('branch'))
        );
    });

    const active = computed(() => {
        if (actor.value !== null) {
            return actor.value.active;
        }
        return sessionContext.isAdminOrSupport;
    });

    function circleKey() {
        if (actor.value) {
            switch (actor.value.ty) {
                case PublicEntityType.Olb:
                    return actor.value.id?.slice(0, 2);
                case PublicEntityType.OlbB:
                case PublicEntityType.Hdp:
                    return actor.value.id?.slice(0, 5);
                default:
                    return federalStateKey.value.slice(2) + '';
            }
        }
    }

    return {
        has,
        init,
        getToken,
        logout,
        canList,
        canCreate,
        canView,
        canUpdate,
        canDelete,
        canManage,
        circleKey,
        active,
        actor,
        contextId,
        federalStateKey,
        selectedContext,
        sessionContext,
        context,
        contextKind,
        contextType,
        activeContextType,
        userType,
        isActiveAsAdmin,
        isChecking,
        isLoading,
        isHealthy,
        federalStateList,
        federalState,
        isApplicant,
        canSeeModifications
    };
});
